<template>
  <div
    id="home"
    dark
    :class="{ active: buzzer.toggle, 'prevent-scroll': isViewOpen }"
    class="pt-14"
  >
    <m-nav
      :toggle="!loading&&!blocker.blocked"
      :pages="pages"
      :menu="menu"
      :user="user"
    />

    <v-btn
      v-show="!loading&&!blocker.blocked"
      large
      :rounded="!view.help.extended"
      color="white secondary--text darken-2"
      :class="['btn help', { 'extended rounded-lg elevation-8': view.help.extended, round: !view.help.extended }]"
      @click="toggleHelp(true)"
    >
      <v-icon>
        {{ icons.help }}
      </v-icon>
      <span class="text font-weight-bold">
        {{ $t('general.help') }}
      </span>
    </v-btn>

    <v-scroll-y-reverse-transition mode="out-in">
      <router-view
        :blocker="blocker"
        :alerts="alerts"
        :user="user"
        :active="active"
        :installed="installed"
        :help="helpContent"
        :dev="dev"
        :ready="!loading"
        :onboard="onboardingAvailable"
      />
    </v-scroll-y-reverse-transition>

    <referrals 
      ref="referrals" 
    />
    
    <profile 
      :toggle="view.profile.toggle"
      :help="helpContent"
      ref="profile" 
      @forceUpdate="toggleUpdate(true)"
    />

    <terms-of-service />

    <privacy-policy />

    <help 
      v-if="helpContent!=null"
      :toggle="view.help.toggle"
      :content="helpContent"
      :alerts="alerts"
      :reviews="view.review.data"
      :hive="hive"
    />

    <v-dialog
      v-model="view.alert.toggle"
      max-width="90vw"
      max-height="80vh"
      persistent
      content-class="alert-dialog"
      overlay-opacity="1"
      z-index="3"
    >
      <v-card 
        v-if="view.alert.notification!=null"
        class="overbar primary--text pt-3"
      >
        <v-card-title class="flex-nowrap">
          <span class="white--text">{{ view.alert.notification.title }}</span>
          <v-spacer />
          <v-icon 
            color="primary"
            class="ml-4"
          >
            {{ icons.message }}
          </v-icon>
        </v-card-title>

        <v-card-text 
          v-html="view.alert.notification.text" 
          ref="alert-text"
          class="alert-dialog-text scrollable text-body-1 pb-12 pt-3"
        />
        <v-btn
          v-show="view.alert.toggle&&view.alert.dismissable"
          fab
          dense
          absolute
          right
          bottom
          dark
          color="grey darken-4"
          @click="toggleAlert(false)"
        >
          <v-icon color="primary">{{ icons.check }}</v-icon>
        </v-btn>
      </v-card>
    </v-dialog>

    <v-overlay
      :value="block"
      color="background"
      opacity="1"
      persistent
      z-index="3"
      class="blocker"
    >
      <v-card 
        class="card pa-4 ma-6 mt-16 black"
      >
        <v-card-title>
          {{ $t(blocker.headline) }}

          <v-btn
            v-if="blocker.dismissable"
            icon
            absolute
            right
            top
            dark
            color="grey"
            @click="blockerAction('secondary')"
          >
            <v-icon>{{ icons.close }}</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text 
          v-html="formatText($t(blocker.text, blocker.params))" 
          class="content scrollable text-body-1 py-2"
        />
        
        <v-card-actions class="pt-4">
          <v-btn
            text
            color="secondary"
            @click="blockerAction('secondary')"
          >
            {{ $t(blocker.actions.secondary) }}
          </v-btn>

          <v-spacer />

          <v-btn
            depressed
            :disabled="blocker.disabled"
            color="secondary"
            class="px-4"
            @click="blockerAction('primary')"
          >
            {{ $t(blocker.actions.ok) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-overlay>

    <update-dialog 
      :toggle="updateApp"
      @toggle="toggleUpdate"
    />

    <onboarding 
      v-if="view.profile.updated"
      v-model="view.onboarding.toggle"
      @close="$router.push('/main')"
    />

    <v-overlay 
      :value="loading"
      color="background"
      fixed
      opacity="1"
      z-index="3"
      class="loading-overlay text-center"
    >
      <div class="d-flex flex-column grey--text pa-8 pt-16">
        <loading 
          class="loading mb-6" 
        />
        <span class="d-block text-overline text-secondary">
          {{ $t('general.loading') }}...
        </span>
        <span 
          class="text-caption mx-2 mt-16"
          v-html="formatText($t('help.email_support'))"
        />
      </div>
    </v-overlay>

    <toast />
  </div>
</template>

<style lang="scss">

  #main {
    padding: 0 !important;
  }
  
  #home {
    padding-top: 56px !important;
    padding-bottom: 64px !important;
    .btn.help {
      position: fixed;
      bottom: calc(var(--safe-area-bottom) + 1.5rem);
      right: 1.5rem;
      z-index: 5;
      transition: all .25s ease-in-out;
      min-width: 44px !important;
      padding: 0 !important;
      .text {
        overflow: hidden;
        max-width: 0;
        padding-left: 0;
        transition: all .25s ease-in-out;
        opacity: 0;
      }
      &.extended {
        padding: 0 16px !important;
        .text {
          opacity: 1;
          padding-left: 8px;
          max-width: 200px;
        }
      }
    }
  }

  #home .home-content {
    position: relative;
    /* margin: var(--safe-area-top) 0 0 var(--safe-area-left) !important; */
  }
  
  #home .page {
    position: relative;
    padding-top: 16px;
  }
  #home .page .empty-state {
    min-height: 80vh;
  }

  .alert-dialog {
    overflow: visible !important;
  }

  .alert-dialog .alert-dialog-text {
    max-height: 60vh;
  }

  .overbar {
    border-top: 4px solid !important;
  }

  .v-content {
    padding-top: 0 !important;
  }

  .v-toolbar .title {
    font-size: 1.125rem !important;
    font-weight: 500;
    letter-spacing: 0.0625rem !important;
    line-height: 1.75rem;
    opacity: .875;
  }

  .blocker .content {
    max-height: calc(60dvh);
  }

  .loading-overlay .loading {
    width: 56px;
    height: 56px;
    opacity: 1;
  }

</style>

<script>
  import { 
    mdiHelpCircleOutline,
    mdiClose,
    mdiCheck,
    mdiMessageAlertOutline,
    mdiGiftOutline,
    mdiSaleOutline,
    mdiHome,
    mdiPoll,
    mdiPlayCircle,
    mdiCurrencyUsd,
    mdiAccountCircle,
  } from '@mdi/js';
  import services from '@/services'
  import { defaults as profileDefaults } from '@/services/profile.js'
  import { pages as links, menu as _menu } from '@/services/home'
  import { get as getBonus } from '@/services/bonus'
  import dictionary from '@/dictionary'
  import { sync } from 'vuex-pathify'
  import { App } from '@capacitor/app';

  export default {
    name: 'Index',

    props: {
      scroll: {
        type: Number,
        default: 0
      },
      height: {
        type: Number,
        default: 0
      },
      active: {
        type: Boolean,
        default: true
      },
      installed: {
        type: Boolean,
        default: false
      },
      modal: {
        type: String,
        default: null
      },
      dev: {
        type: Boolean,
        default: false
      }
    },

    components: {
      mNav: () => import('@/components/mNav'),
      Profile: () => import('./Profile'),
      Referrals: () => import('./Referrals'),
      Help: () => import('@/views/help/Help'),
      Toast: () => import('@/components/Toast'),
      TermsOfService: () => import('@/views/help/terms/Index'),
      PrivacyPolicy: () => import('@/views/help/privacy/Index'),
      UpdateDialog: () => import('@/components/UpdateDialog'),
      Loading: () => import('@/components/IconLoading'),
      Onboarding: () => import('./Onboarding'),
    },

    data: () => ({
      api: process.env.VUE_APP_ROOT_API,
      icons: {
        help: mdiHelpCircleOutline,
        close: mdiClose,
        check: mdiCheck,
        message: mdiMessageAlertOutline,
        bonus: mdiGiftOutline,
        club: mdiSaleOutline,
        home: mdiHome,
        account: mdiAccountCircle,
        dashboard: mdiPoll,
        video: mdiPlayCircle,
        payments: mdiCurrencyUsd,

      },
      date: {
        now: null,
        format: 'DD/MM',
        keeper: null,
        updatedAt: null
      },
      links,
      view: {
        alert: {
          toggle: false,
          notification: null,
          dismissable: false
        },
        review: {
          loading: false,
          updatedAt: null,
          dismissedAt: null,
          data: {
            history: [],
            reviewedAt: '',
            reviewedBefore: '',
          }
        },
        profile: {
          toggle: false,
          updated: false,
          updatedAt: null
        },
        notifications: {
          toggle: false,
          updated: false,
          updatedAt: null
        },
        help: {
          toggle: false,
          updated: false,
          updatedAt: null,
          extended: null,
        },
        onboarding: {
          toggle: null,
          content: null
        },
      },
      blocker: {
        toggle: false,
        blocked: true,
        headline: 'home.blocker.review.title',
        text: 'home.blocker.review.text',
        params: {},
        actions: {
          ok: 'general.ok',
          secondary: 'general.help',
        },
        blockedBy: null,
        disabled: false
      },
      dictionary,
      helpContent: null,
      checklistAt: null,
      legal: null,
      updateApp: false,
      appURL: process.env.VUE_APP_ROOT,
    }),

    computed: {
      views: sync('app/views'),
      referrals: sync('app/views@referrals'),
      payments: sync('app/views@payments'),
      bonus: sync('app/views@bonus'),
      profile: sync('app/views@profile'),
      help: sync('app/views@help'),
      calendar: sync('app/views@help.calendar'),
      ticket: sync('app/views@help.ticket'),
      tickets: sync('app/views@help.tickets'),
      events: sync('events/data@items'),
      event: sync('events/data@selected'),
      advertising: sync('app/views@help.advertising'),
      buzzer: sync('app/buzzer'),
      user: sync('user'),
      toast: sync('app/toast'),
      version: sync('app/version'),
      config: sync('app/config'),

      pages () {
        const config = this.config;
        const profile = this.user.profile;
        let pages = _.cloneDeep(links);
        if (!!config&&'pages' in config) {
          const config_pages = _.cloneDeep(this.getContent(profile, config.pages));
          if (config_pages && config_pages!='default') pages = config_pages;
        }
        pages = _.reduce(pages, (comp, page, p) => {
          if (!('partner' in page)||page.partner==profile.partner) comp[p] = { ...page, icon: this.icons[page.icon]||page.icon };
          return comp;
        }, {});
        return pages;
      },

      menu () {
        const config = this.config;
        const profile = this.user.profile;
        let menu = _.cloneDeep(_menu);
        if (!!config&&'menu' in config) {
          const config_menu = _.cloneDeep(this.getContent(profile, config.menu));
          if (config_menu && config_menu!='default') menu = config_menu;
        }
        menu = _.reduce(menu, (comp, page, p) => {
          if (!('partner' in page)||page.partner==profile.partner) comp.push({ ...page, icon: this.icons[page.icon]||page.icon });
          return comp;
        }, []);
        return menu;
      },

      loading () {
        const reviews = this.view.review.updatedAt;
        const profile = this.view.profile.updatedAt;
        const help = !this.help.loading;
        return _.some([reviews, profile, help], resource => !resource);
      },

      isViewOpen () {
        return _.some(this.view, view => _.has(view, 'toggle')&&view.toggle);
      },

      reviewCheck () {
        const signedupAt = this.$moment.utc(this.user.statusChange).local().format('YYYY-MM-DD');
        const reviewedAt = this.view.review.data.reviewedAt;

        const tickets = this.tickets;
        const online = _.find(_.omitBy(tickets, 'closed'), t => {
          return t.section=='review'&&_.has(t.category, 'child')&&t.category.child=='Revisão Online';
        });

        const total = _.isEmpty(signedupAt)||_.isNil(signedupAt) ? 0 : this.$moment().diff(signedupAt, 'd');
        const last = _.isNil(reviewedAt) || _.isEmpty(reviewedAt) ? total : this.$moment().diff(reviewedAt, 'd');
        const expiresIn = 30-last;
        const expiresInText = this.$moment.duration(expiresIn, 'days').humanize();
        const required = last>=20;
        const delayable = expiresIn<=10 && expiresIn>5;
        const expired = expiresIn<=0&&!this.tester;
        const expiresAt = this.$moment().add(expiresIn, 'days').format('YYYY-MM-DD');
        const onlineStatus = !_.isNil(online) ? _.has(online.data, 'status') ? online.data.status : 0 : null;
        const i18n_path = 'home.alerts.review.';
        return {
          required: required&&!this.tester,
          title: this.$t(i18n_path+(!required||onlineStatus==1 ? 'title' : 'cta_title')),
          text: this.$t(i18n_path+
            (onlineStatus!=null ? 
              (onlineStatus==1 ? 'evaluating' : 
                onlineStatus==2 ? 'problem' : 'pending') : 
              !required ? 'up_to_date' : 
                (expired ? 'expired' : 'required')), { days: expiresInText, cta: this.$t(i18n_path+'cta') }),
          expired: expired||onlineStatus==2,
          expiresAt,
          expiresIn,
          delayable,
          online: onlineStatus,
          last,
        }
      },

      alerts () {
        let tickets = this.tickets;
        tickets = _.omitBy(tickets, 'closed');
        let alerts = [];
        const sections = this.helpContent;
        let pedingScheduledTicket = false;
        const urgent = this.urgent;

        if (!_.isEmpty(tickets)&&!!sections) {
          _.each(_.filter(tickets, ['closed', false]), t => {
            const last = _.last(t.messages);
            const script = sections[t.section];
            if ((_.has(last, 'support')&&last.support&&!last.read)||_.has(last, 'assignee')&&last.assignee=='MEL') {
              alerts.push({
                issue: t.section,
                ticket: t.id,
                type: 'message',
                priority: 70,
                title: this.$t('home.alerts.new_message'),
                text: this.$t('home.alerts.new_support_message')
              })
            }else if ((_.has(t.data, 'status')&&t.data.status==0)||(_.has(script, 'props')&&_.has(script.props, 'alert')&&(_.isArray(script.props.alert) ? _.some(script.props.alert, a => t.data[a.key]==a.value) : _.has(t.data, script.props.alert.key)&&t.data[script.props.alert.key]==script.props.alert.value))) {
              pedingScheduledTicket = pedingScheduledTicket ? pedingScheduledTicket : _.has(t.data, 'type')&&t.data.type=='scheduled';
              // Some tickets may have category === null; guard access
              const hasCategory = t && t.category && t.category.value && t.category.child;
              const title = hasCategory ? this.$t('help.categories.'+t.category.value+'.'+t.category.child) : t.summary;
              let message = this.$t('home.alerts.pending_ticket', { title });
              let expiration = null;
              if (_.has(script, 'props')&&_.has(script.props, 'expiration')) {
                expiration = this.$moment(this.$moment.utc(t.createdAt).add(script.props.expiration[0], script.props.expiration[1])).toNow(true);
                message = this.$t('home.alerts.expiring_ticket', { title, expiration });
              }
              if (_.has(script, 'props')&&_.has(script.props, 'alert')&&(t.data[script.props.alert.key]==script.props.alert.value||_.some(script.props.alert, a => t.data[a.key]==a.value))) {
                const alert = _.isArray(script.props.alert) ? _.find(script.props.alert, a => t.data[a.key]==a.value) : script.props.alert;
                message = _.has(script.props, 'expiration') ? alert.message.replace(`{{${script.props.expiration[1]}}}`, expiration) : alert.message;
              }
              if (_.isNil(urgent)||t.id!=urgent.id) {
                alerts.push({
                  issue: t.section,
                  ticket: t.id,
                  type: 'message',
                  priority: 55,
                  title: this.$t('help.support'),
                  text: message
                })
              }
            }
          })
        }
        const calendar = this.calendar;
        const profile = this.user.profile;

        const modem = this.buzzer.cached > 10000;
        const reviewCheck = profile.partner ? this.reviewCheck : null;
        
        if (!_.isNil(calendar)&&this.$moment().isBefore(calendar.start)) { 
          const absences = _.reduce(profile.buzzer.absences, (count, absence) => {
            const c = _.filter(absence, element => {
              return this.$moment().subtract(90, 'days').isSameOrBefore(element)
            })
            return c.length + count
          }, 0) 
          alerts.push({
            issue: calendar.issue,
            ticket: _.has(calendar, 'ticket') ? calendar.ticket : null,
            type: 'calendar',
            priority: 60,
            title: calendar.title,
            text: calendar.text,
            absences
          });
        }else{
          if (!_.isNil(urgent)) {
            alerts.push({
              issue: 'urgent',
              ticket: urgent.id,
              type: 'urgent',
              critical: true,
              priority: 80,
              title: this.$t('home.alerts.urgent_title'),
              text: this.$t('home.alerts.urgent_text'),
            });
          }else if (modem) {
            alerts.push({
              issue: 'modem',
              type: 'urgent',
              critical: true,
              required: true,
              priority: 70,
              title: this.$t('home.alerts.modem_title'),
              text: this.$t('home.alerts.modem_text'),
            });
          }else if (!!reviewCheck) {
            const loading = this.help.loading;
            alerts.push({
              issue: 'review',
              type: 'review',
              critical: !(!reviewCheck.required||reviewCheck.online==1),
              expired: reviewCheck.expired,
              required: reviewCheck.required,
              priority: reviewCheck.required ? 50 : 30,
              hidden: !reviewCheck.required||pedingScheduledTicket,
              title: reviewCheck.title,
              text: reviewCheck.text,
              data: { ...reviewCheck },
              loading
            });
          }
        }

        if (this.nextEvent) {
          const event = this.nextEvent;
          const title = this.dictionary.events[event.type] +' '+ event.title;
          const start = event.mobilizesAt || event.startsAt;
          alerts.push({
            event: event.id,
            type: 'event',
            priority: 60,
            title: this.$t('events.title'),
            text: _.every(['acceptedAt', 'declinedAt', 'uninvitedAt'], (k) => event[k]==null) ? 
              this.$t('events.invitation', { title, date: this.$moment(start).isValid() ? ` *${this.$tc('general.day')} `+this.$moment(start).format('DD/MM HH:mm')+'*' : '' }) : 
              start==null||this.$moment().add(1,'h').isBefore(start) ? 
              this.$t('events.confirmed', { title, date: this.$moment(start).isValid() ? ` *${this.$tc('general.day')} `+this.$moment(start).format('DD/MM HH:mm')+'*' : '' }) : 
              this.$tc('events.ongoing', this.$moment().isBefore(start) ? 1 : 2, { title, start: this.$moment(start).format('HH:mm') })
          });
        }

        if (sections?.uninstall?.hidden===false) {
          const uninstallTicket = this.findTicket('uninstall');
          if (!uninstallTicket) {
            alerts.push({
              type: 'urgent',
              priority: 100,
              title: this.$t('calendar.uninstall'),
              text: this.$t('calendar.uninstall_alert'),
              open: {
                view: 'help',
                section: 'uninstall'
              }
            });
          }
        }

        if (profile.partner&&!!sections&&('13' in sections.manual.items)) {
          const legal = this.legal;
          this.setStorage('legal', Date.now());
          const priority = legal==null||this.$moment().add(1, 'week').isBefore(parseInt(legal)) ? 60 : 0;
          alerts.push({
            type: 'info',
            outlined: !priority,
            priority,
            title: this.$t('home.alerts.legal_title'),
            text: this.$t('home.alerts.legal_text'),
            color: !!legal ? {
              title: 'secondary--text text--lighten-1',
              text: 'white--text',
              background: 'secondary darken-4'
            } : null,
            click: ($) => {
              $.open({
                type: 'info',
                open: {
                  view: 'help',
                  section: 'manual',
                  item: '13'
                }
              });
            }
          });
        }

        const bonus = this.bonus.current;
        if (bonus) {
          alerts.push({
            type: 'info',
            priority: 10,
            title: this.$t('bonus.next'),
            text: this.$t('bonus.current', { date: bonus.dateDraw }),
            to: { name: 'bonus' },
            color: {
              title: 'secondary--text text--lighten-1',
              text: 'white--text',
              background: 'secondary darken-4'
            }
          });
        } 

        if (this.advertising.toggle) {
          alerts.push({
            issue: 'advertising',
            type: 'schedule',
            priority: 50,
            title: this.$t('calendar.slots.PUB'),
            text: this.$t('calendar.schedule_now')
          });
        } 
        return alerts;
      },

      nextEvent () {
        const events = _.reject(this.events, (e) => _.isNil(e));
        const next = _.reduce(_.filter(events, event => {
          const status = _.first(event.drivers);
          return !!status&&!!status.invitedAt&&(status.declinedAt==null&&status.uninvitedAt==null);
        }), (next, event) => {
          next = (this.$moment().isBefore(event.demobilizesAt||event.endsAt))&&(next==null||this.$moment(event.mobilizesAt||event.startsAt).isBefore(next.mobilizesAt||event.startsAt)) ? event : next;
          return next;
        }, null);
        return next;
      },

      urgent () {
        const tickets = _.omitBy(this.tickets, 'closed');
        return _.find(tickets, ['section', 'urgent']);
      },

      inactivity () {
        const tickets = _.omitBy(this.tickets, 'closed');
        return _.find(tickets, ['section', 'inactivity']);
      },

      survey () {
        const tickets = _.omitBy(this.tickets, 'closed');
        return _.find(tickets, { section: 'survey', data: { status: 0 }})
      },

      block () {
        const updateApp = this.updateApp;
        const alert = this.view.alert.toggle;
        const blocker = this.blocker.toggle;
        const partner = this.user.profile.partner;
        return blocker && !updateApp && !alert && partner;
      },

      opacity () {
        const limit = 128;
        const opacity = this.scroll < limit ? (this.scroll / limit) - .16 : .84;
        return opacity;
      },

      hive () {
        const profile = this.user.profile;
        const hive = _.isNil(profile.hive) ? profileDefaults.hive : profile.hive;
        const uri = 'geo:0,0?q=' + _.replace(_.join([hive.address, hive.city], ', '), ' ', '+');
        return {
          ...hive,
          uri
        }
      },

      onboardingAvailable () {
        const config = this.config;
        return !!config && !!this.getContent(this.user.profile, config?.onboarding?.contents);
      },

      tester () {
        const profile = this.user.profile;
        return (profile?.tags||[]).includes('mobees');
      },

    },

    watch: {
      modal: {
        immediate: true,
        handler (view) {
          if (!!view&&_.has(this.view, view)) {
            // open view
            if (view=='help') {
              const route = this.$route;
              this.view.help.section = route.query.section;
              this.view.help.context = route.name;
            }
            this.view[view].toggle = true;
          }else{
            _.each(this.view, view => {
              if (_.has(view, 'toggle')&&view.toggle) view.toggle = false;
            })
          }
        }
      },
      active: {
        immediate: true,
        handler (active) {
          if (active) {
            // update Home data
            if (!this.view.review.loading) this.updateData();
          }
        }
      },
      loading (b) {
        if (!b){
          this.$nextTick(() => {
            this.toggleBlocker();
          });
          if (this.view.help.extended===null) {
            setTimeout(() => {
              this.view.help.extended = true;
              setTimeout(() => {
                this.view.help.extended = false;
              }, 4000);
            }, 4000);
          }
        }
      },
      // 'view.review.history' (reviews) {
      //   this.toggleBlocker();
      // },
      help (help) {
        // this.toggleBlocker();
        const config = this.config;
        this.view.onboarding.content = this.getContent(this.user.profile, config?.onboarding?.contents);
      },
      'user.profile' (profile) {
        // this.toggleBlocker();
      },
      tickets: {
        deep: true,
        handler (tickets) {
          // this.toggleChecklist();
        }
      },
      nextEvent: {
        deep: true,
        handler (event) {
          if (!!event&&event.type=='SWR') this.toggleEvent(event.id);
        }
      },
      helpContent: {
        immediate: true,
        deep: true,
        handler (content) {
          if (!!content&&_.has(content.controller, 'maintenance')&&content.controller.maintenance) {
            this.$router.replace({ path: '/maintenance' });
          }
        }
      },
      survey: {
        immediate: true,
        handler (survey) {
          const view = this.view.alert;
          if (!_.isNil(survey)) {
            if (_.has(this.helpContent, 'survey')) {
              const text = _.has(survey, 'data') && _.has(survey.data, 'alert') ? survey.data.alert : _.find(this.helpContent.survey.props.alert, { key: 'status', value: 0 }).message;
              const alert = {
                survey: true,
                title: survey.category.child,
                reference: 'ticket#'+survey.id,
                text,
              }
              this.toggleAlert(true, alert);
            }
          }else if (_.isNil(survey)&&view.toggle&&view.notification.survey===true) {
            view.toggle = false;
          }
        }
      }
    },

    methods: {
      ...services,

      toggleChecklist () {
        if (this.dev) return;
        const checklistAt = this.checklistAt;
        const signedupAt = this.user.statusChange;
        const checklist = this.helpContent?.checklist;
        if (!!checklist&&checklistAt===null&&!!this.tickets) {
          const ticket = _.find(this.tickets, ticket => ticket.section=='checklist'&&(ticket.closed||ticket.data.status==1));
          const daysSinceSignup = this.$moment().diff(signedupAt, 'd');
          const checklist = daysSinceSignup>=1 && !!ticket || daysSinceSignup<=15;
          if (!checklist) {
            if (this.$route.query.section!='checklist') this.toggleHelp(true, null, 'checklist');
          }else{
            this.setStorage('checklistAt', this.$moment().valueOf());
          }
        }
      },

      toggleBlocker () {
        const profile = this.profile.pending;
        const partner = this.user.profile.partner;
        const hive = this.hive;
        const review = this.view.review.updatedAt==null ? null : this.reviewCheck;
        const urgent = this.urgent;
        const inactivity = this.inactivity;
        const calendar = _.isNil(this.calendar)||this.$moment().isAfter(this.calendar.start) ? null : this.calendar;
        const dismissable = _.isNil(urgent)&&!_.isNil(review)&&review.delayable;
        const dismissedAt = this.view.review.dismissedAt;
        const blockedBy = {
          review: !_.isNil(review)&&(review.expired||(review.required&&((_.isNil(review.online) ? _.isNil(calendar) : review.online!=1)&&(!dismissable||_.isNil(dismissedAt)||this.$moment().diff(dismissedAt, 'days')>=1)))),
          issue: (!_.isNil(urgent)&&(_.isNil(calendar)||calendar.issue!='urgent'))&&!_.isNil(inactivity),
          profile: false
        }
        // log exactly why review is blocked
        console.log('blocked', this.blocker.toggle, blockedBy);

        if (this.dev) {
          this.blocker.toggle = false;
          this.blocker.blockedBy = blockedBy;
          this.blocker.blocked = false;
          return;
        }

        // block app funcionalities w/ dialog
        this.blocker.toggle = partner&&!this.help.toggle&&(blockedBy.profile||blockedBy.review||blockedBy.issue);
        this.blocker.blockedBy = blockedBy;
        this.blocker.blocked = this.blocker.toggle && (blockedBy.profile||(blockedBy.review&&!dismissable)||blockedBy.issue);
        // console.log('blockedBy', blockedBy, this.blocker.toggle);
        if (blockedBy.profile) {
          this.blocker.headline = 'home.blocker.profile.title';
          this.blocker.text = 'home.blocker.profile.text';
          this.blocker.actions.ok = 'general.refresh';
          this.blocker.actions.secondary = 'general.help';
        }else if (urgent) {
          this.blocker.headline = 'home.blocker.urgent.title';
          const scheduled = urgent&&!_.isNil(calendar)&&calendar.issue=='urgent';
          this.blocker.text = scheduled ? 'home.blocker.urgent.scheduled' : 'home.blocker.urgent.text';
          if (scheduled) {
            this.blocker.params = { 
              day: this.$moment(calendar.start).format('DD/MM'), 
              hour: this.$moment(calendar.start).hour(), 
              url: hive.uri, 
              address: hive.address 
            }
          }
          this.blocker.actions.ok = scheduled ? 'general.scheduled' : 'general.schedule';
          this.blocker.actions.secondary = 'general.help';
          this.blocker.disabled = scheduled;
        }else if (this.blocker.toggle&&!_.isNil(calendar)&&this.$moment().isBefore(calendar.start)) {
          this.blocker.headline = calendar.title;
          this.blocker.text = 'home.blocker.urgent.scheduled';
          this.blocker.params = { 
            day: this.$moment(calendar.start).format('DD/MM'), 
            hour: this.$moment(calendar.start).hour(), 
            url: hive.uri, 
            address: hive.address 
          }
          this.blocker.actions.ok = 'general.scheduled';
          this.blocker.actions.secondary = 'general.help';
          this.blocker.disabled = true;
        }else if (!_.isNil(review)&&review.expired&&review.online==2) {
          this.blocker.text = 'home.blocker.review.problem';
          this.blocker.disabled = false;
          this.blocker.actions.ok = 'general.schedule';
          this.blocker.actions.secondary = 'general.help';
        }else if (!_.isNil(review)&&review.required) {
          this.blocker.dismissable = dismissable;
          if (dismissable) this.blocker.actions.secondary = 'general.later';
          this.blocker.headline = 'home.blocker.review.alt_title';
          this.blocker.text = 'home.blocker.review.text';
          this.blocker.params = {
            days: review.expiresIn,
            plural: review.expiresIn>1 ? 's' : ''
          }
        }else if (!_.isNil(review)&&review.online==1) {
          this.blocker.headline = 'home.blocker.review.title';
          this.blocker.text = 'home.blocker.review.pending';
          this.blocker.actions.ok = 'general.wait';
          this.blocker.disabled = true;
        }else if (inactivity) {
          this.blocker.headline = 'home.blocker.inactivity.title';
          this.blocker.text = 'home.blocker.inactivity.text';
          this.blocker.actions.ok = 'general.ok';
          this.blocker.actions.secondary = 'general.help';
        }
        if (!dismissable) this.blocker.actions.secondary = 'general.help';
      },

      toggleAlert (b, notification) {
        if (this.updateApp) return;

        const alert = this.view.alert;
        if (b) {
          alert.notification = notification;
          alert.notification.text = this.formatText(notification.text);
          this.$nextTick(() => {
            this.$refs['alert-text'].addEventListener('click', (e) => {
              console.log(e);
              if ('open' in e.target.dataset) {
                e.preventDefault();
                this.open(e.target.href);
              }
            });
          })
          setTimeout((alert) => {
            alert.dismissable = true;
          }, 3000, alert);
        }else{
          if (_.has(alert.notification, 'id')) this.setNotifications(alert.notification);
          const reference = alert.notification.reference;
          if (reference!=null) {
            const [type, id] = _.split(reference, '#');
            if (type=='ticket') {
              this.toggleTicket(id);
            }else if (type=='flashmob') {
              this.toggleEvent(id);
            }
          }
          alert.dismissable = false;
        }
        alert.toggle = b;
      },

      blockerAction (trigger) {
        const controller = this.blocker;
        if (trigger=='primary') {
          if (controller.blockedBy.profile) {
            this.toggleView('profile');
          }else if (!_.isNil(this.urgent)) {
            if (_.isNil(this.calendar)||_.isNil(this.calendar.ticket)) {
              this.toggleTicket(this.urgent.id);
            }else{
              this.toggleTicket(this.calendar.ticket);
            }
          }else if (!_.isNil(this.inactivity)) {
            this.toggleTicket(this.inactivity.id);
          }else{
            this.toggleHelp(true, 'review', 'review');
          }
        }else{
          if (this.blocker.dismissable) {
            this.view.review.dismissedAt = this.$moment().startOf('day').valueOf();
            this.blocker.toggle = false;
          }else{
            this.toggleHelp(true);
          }
        }
      },

      findTicket (section, status=null, closed=null) {
        const tickets = this.tickets;
        return _.find(tickets, ticket => {
          // console.log(ticket.closedAt, this.$moment().diff(ticket.closedAt, 'd'));
          return (closed==null||ticket.closed==closed) && ticket.section==section && (status===null||(ticket.data?.status==status));
        });
      },

      toggleUpdate (b) {
        this.updateApp = b;
      },

      setReviews (data) {
        this.view.review.loading = true;
  
        const reviews = _.isNil(data.revisoes) ? [] : _.orderBy(data.revisoes, ['data'], ['desc']);
        this.view.review.data.history = reviews;
        if (!_.isEmpty(reviews)) {
          this.view.review.data.reviewedAt = reviews[0].data;
          if (_.size(reviews)>=2) this.view.review.data.reviewedBefore = reviews[1].data;
        }
        this.view.review.updatedAt = this.$moment().valueOf();
        this.toggleBlocker();
        setTimeout(($) => {
          $.view.review.loading = false;
        }, 500, this);
      },

      getNotifications () {
        this.views.notifications.loading = true;
        const cpf = this.rawCPF(this.user.cpf);
        const token = this.user.auth.token;

        this.$api
          .get('/getdrivernotifications/'+cpf+'/'+token+'/?format=json')
          .then(response => {
            console.log('getNotifications => ', response);
            if(response.data.retorno!=400){
              const notifications = _.mapValues(_.keyBy(response.data.notifications, 'id'), notification => {
                return {
                  ...notification,
                  sent: this.$moment.utc(notification.sent).local().format('YYYY-MM-DD HH:mm:ss')
                }
              });
              const alert = _.find(notifications, { type: 'ALERT', read: null });
              if (!_.isNil(alert)) {
                this.toggleAlert(true, alert);
              }
              this.views.notifications.items = Object.assign({}, notifications);
              this.views.notifications.updatedAt = this.$moment().valueOf();
              this.views.notifications.loading = false;
            }else{
              // this.toggleToast(
              //   true,
              //   'Erro ao carregar notificações',
              //   5000,
              //   false,
              // );
            }
          })
          .catch(error => {
            setTimeout(($) => {
              $.getNotifications();
            }, 7000, this);
            this.handleError(error);
          })
          .finally(() => {
          });
      },

      setNotifications (read) {
        const cpf = this.rawCPF(this.user.cpf);
        const token = this.user.auth.token;

        read = _.map(_.isArray(read) ? read : [read], n => {
          return { id: n.id, read: this.$moment.utc().format('YYYY-MM-DD HH:mm:ss') }
        })

        this.$api
          .post('/setdrivernotifications/'+cpf+'/'+token+'/?format=json', read)
          .then(response => {
            console.log('setNotifications => ', response);
            if (response.data.retorno!=401) {
              const notifications = _.keyBy(read, 'id');
              this.views.notifications.items = _.merge({}, this.views.notifications.items, notifications);
            }
            this.view.alert.notification = null;
          })
      },

      getEvents () {
        const cpf = this.rawCPF(this.user.cpf);
        const token = this.user.auth.token;

        const data = {
          cpf,
          token,
        };
        
        console.log('getEvents...', data);
        this.$api
          .get('driver/events/', { params: data })
          .then(response => {
            console.log('getEvents => ',response);

            this.events = Object.assign({}, _.keyBy(_.map(response.data, item => {
              const event = this.format(item, this.dictionary.events);
              const driver = _.first(event.drivers) || {};
              const { acceptedAt=null, declinedAt=null, invitedAt=null, uninvitedAt=null } = driver;
              return {
                ...event,
                acceptedAt, 
                declinedAt, 
                invitedAt, 
                uninvitedAt
              }
            }), 'id'));
            console.log('events', this.events);
          })
          .catch(error => {
            setTimeout(($) => {
              $.getEvents();
            }, 7000, this);
            this.handleError(error);
          })
      },

      updateData (force) {
        if (force===true||this.date.updatedAt==null||this.$moment().diff(this.date.updatedAt, 'minutes')>5) {
          this.date.updatedAt = this.$moment().valueOf();
          this.getProfile();
          this.getNotifications();
          this.loadHelpContent(false);
        }
      },

      getProfile () {
        this.$nextTick(() => {
          if (_.has(this.$refs, 'profile')) {
            new Promise((resolve) => {
              this.$refs['profile'].getProfile(resolve);
            }).then((data) => {
              console.log(data);
              this.setReviews(data);
              this.view.profile.updated = true;
              this.view.profile.updatedAt = this.$moment().valueOf();

              this.getEvents();
              this.getBonus();
            });
          }else{
            setTimeout(($) => {
              $.getProfile();
            }, 200, this);
          }
        })
      },

      getBonus () {
        const cpf = this.rawCPF(this.user.cpf);
        const token = this.user.auth.token;
        
        getBonus(cpf, token, this).then((data) => {
          if (!!data) {
            const { prefix, items } = data;
            this.bonus.prefix = prefix;
            this.bonus.items = items;
            const first = _.first(_.orderBy(items, ['dateClosed'], ['desc']))
            if (first&&!first.dateClosed) this.bonus.current = first;
          }
        })
      }
    },

    mounted () {
      // set GA UserType dimension
      this.$gtag.customMap({ 'dimension1': 'UserType' });
      this.$gtag.set({
        'UserType': 'Parceiro'
      });

      this.toggleToast(false);

      this.getStorage('checklistAt').then(value => {
        this.checklistAt = value;
      });

      this.date.keeper = setInterval(() => {
        this.date.now = this.$moment();
      }, 30000);

      this.getStorage('legal').then(v => {
        this.legal = v;
      });

      this.loadHelpContent(false);

      App.addListener('appRestoredResult', ({ pluginId, methodName, data, success }) => {
        console.log('Restored state:', pluginId, methodName, JSON.stringify(data));
        this.trackEvent('state', `${pluginId} ${methodName}`);
        this.getStorage('camera').then(state => {
          if (!!state) {
            state.data = data;
            this.setStorage('camera', state).then(() => {
              this.toggleTicket(state.ticket);
            });
          }
        });
      });
    },
    beforeDestroy () {
      clearInterval(this.date.keeper);
    }
  }
</script>
